import {
  type CoreDslFormLeafPartial,
  type CoreDslFormOperator,
  hasSelectOption,
  type ModelProperty,
  type PropertyType,
} from '@salescore/core'
import { Form, Select } from 'antd'
import { Array, pipe } from 'effect'
import { t } from 'i18next'
import { type ReactElement, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { getCoreDslFormOperatorLabel } from '../../../../../domain/operatorLabel'

const fieldTypeToFilterTypes = (type: PropertyType): CoreDslFormOperator[] => {
  switch (type) {
    case 'string': {
      // 比較系は選択肢のときのみ
      return [
        '=',
        '!=',
        '<',
        '>',
        'present',
        'blank',
        'in',
        'not_in',
        'include',
        'not_include',
        'starts_with',
        'not_starts_with',
      ]
    }
    case 'integer': {
      return ['=', '!=', '<', '>', '>=', '<=', 'not_null', 'null']
    }
    case 'numeric': {
      return ['=', '!=', '<', '>', '>=', '<=', 'not_null', 'null']
    }
    case 'date': {
      return ['=', '!=', '<', '>', '>=', '<=', 'not_null', 'null']
    }
    case 'datetime': {
      return ['=', '!=', '<', '>', '>=', '<=', 'not_null', 'null']
    }
    case 'time': {
      return ['=', '!=', '<', '>', '>=', '<=', 'not_null', 'null']
    }
    case 'boolean': {
      return ['=', '!=', 'not_null', 'null']
    }
    case 'array': {
      return ['present', 'blank', 'overlap', 'not_overlap', 'include', 'not_include', 'in', 'not_in']
    }
    case 'object': {
      return []
    }
  }
}

// ラベルの中身は domain/operatorLabel.ts を正本とし、ここは翻訳関数の購読だけを担う。
const useGetLabel = (): ((type: PropertyType | undefined, operator: CoreDslFormOperator) => string) => {
  const { t } = useTranslation()
  return useCallback(
    (type: PropertyType | undefined, operator: CoreDslFormOperator): string =>
      getCoreDslFormOperatorLabel(t, operator, type),
    [t],
  )
}

export const CoreDslFormOperatorFormItem = ({
  leaf,
  onChange,
}: {
  onChange: (x: CoreDslFormLeafPartial) => void
  leaf: CoreDslFormLeafPartial
}): ReactElement => {
  // if (leaf.type === 'field') {
  //   return <></>
  // }
  const property = leaf.left === undefined || leaf.left.type === 'literal' ? undefined : leaf.left.property
  const getLable = useGetLabel()
  const options = useMemo(() => getOptions(property, getLable), [getLable, property])

  useEffect(() => {
    // 選択中のfilterTypeが、選択可能なオプションの中に含まれなかったらクリア
    if (leaf.operator !== undefined && !options.some((option) => option.value === leaf.operator)) {
      onChange({
        ...leaf,
        operator: undefined,
        right: undefined,
      })
    }
  }, [options])

  return (
    <Form.Item label={t(`条件`)} rules={[{ required: true, message: t(`入力してください`) }]}>
      <Select
        // 条件セレクトは狭いグリッド列に入るため、既定(popupMatchSelectWidth=true)だと
        // ドロップダウンがトリガー幅に切り詰められ、長いラベル(「次の要素を全ては含まない」等)が truncate される。
        // false で内容幅に合わせて広げる。
        popupMatchSelectWidth={false}
        disabled={options.length === 0}
        value={leaf.operator}
        onChange={(value) => {
          onChange({
            ...leaf,
            operator: value,
          })
        }}
        options={options}
      />
    </Form.Item>
  )
}

const getOptions = (
  property: ModelProperty | undefined,
  getLabel: (type: PropertyType | undefined, operator: CoreDslFormOperator) => string,
): Array<{
  label: string
  key: string
  value: CoreDslFormOperator
}> =>
  property === undefined
    ? []
    : pipe(
        fieldTypeToFilterTypes(property.type),
        Array.filter((operator) => {
          // 一部のフィルタ種別については、条件を満たしたときのみ表示する
          switch (operator) {
            // 選択肢があるときだけin,not_inを表示する
            case 'in': {
              return hasSelectOption(property)
            }
            case 'not_in': {
              return hasSelectOption(property)
            }
            case '>': {
              // 文字列で指定されている比較演算子は、選択肢があるときのみ
              return property.type !== 'string' || hasSelectOption(property)
            }
            case '<': {
              // 文字列で指定されている比較演算子は、選択肢があるときのみ
              return property.type !== 'string' || hasSelectOption(property)
            }
            default: {
              return true
            }
          }
        }),
        Array.map((operator, index) => ({
          label: getLabel(property.type, operator),
          key: `${operator}-${index}`, // filterTypeに重複がでてしまいkeyが重複すると表示がバグるので、iをいれておく
          value: operator,
        })),
      )
